'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/features/auth';
import { isSafeRedirect } from '@/lib/security';

// Ruta fallback si el redirect provisto no es seguro
const REDIRECT_FALLBACK = '/dashboard';

const APP_NAME     = process.env.NEXT_PUBLIC_APP_NAME     || 'Portal';
const APP_INITIALS = APP_NAME.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2) || 'PO';

const loginSchema = z.object({
  destino: z.string().min(1, 'Ingresa tu email o teléfono').refine(
    (val) => {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      const isPhone = /^\+?[\d\s-]{10,}$/.test(val.replace(/\D/g, ''));
      return isEmail || isPhone;
    },
    { message: 'Ingresa un email o teléfono válido' }
  ),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectParam = searchParams.get('redirect') || REDIRECT_FALLBACK;
  const [isLoading, setIsLoading] = useState(false);
  // Redirect validado contra isSafeRedirect. Se calcula en useEffect para evitar
  // referencias a window durante SSR (isSafeRedirect usa window.location.origin).
  const [redirectSeguro, setRedirectSeguro] = useState<string>(REDIRECT_FALLBACK);

  useEffect(() => {
    if (isSafeRedirect(redirectParam)) {
      setRedirectSeguro(redirectParam);
    } else {
      setRedirectSeguro(REDIRECT_FALLBACK);
    }
  }, [redirectParam]);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { destino: '' },
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const response = await authApi.solicitarOtp({ destino: data.destino });
      if (response.success && response.data) {
        const tipoMensaje = response.data.tipo === 'email'
          ? 'Revisá tu correo electrónico'
          : 'Revisá tu WhatsApp';
        toast.success(`Código enviado. ${tipoMensaje}`);
        sessionStorage.setItem('jf-otp-destino', data.destino);
        sessionStorage.setItem('jf-otp-tipo', response.data.tipo || 'email');
        router.push(`/auth/verificar?redirect=${encodeURIComponent(redirectSeguro)}`);
      } else {
        toast.error(response.error || 'Error al enviar el código');
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Error al enviar el código');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="w-full max-w-md">

        {/* Logo / Marca */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--tenant-primary, #FF6B35), var(--tenant-gradient-end, #FF8E53))' }}
            >
              {APP_INITIALS}
            </div>
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {APP_NAME}
            </span>
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Bienvenido</CardTitle>
            <CardDescription>Ingresá tu email o teléfono para acceder</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="destino">Email o Teléfono</Label>
                <div className="relative">
                  <Input
                    id="destino"
                    type="text"
                    placeholder="nombre@empresa.com o +54 11 1234-5678"
                    className="pl-10 h-12"
                    disabled={isLoading}
                    {...form.register('destino')}
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {form.watch('destino')?.includes('@')
                      ? <Mail className="h-5 w-5" />
                      : <Phone className="h-5 w-5" />}
                  </div>
                </div>
                {form.formState.errors.destino && (
                  <p className="text-sm text-destructive">{form.formState.errors.destino.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-white font-medium"
                style={{ background: 'linear-gradient(135deg, var(--tenant-primary, #FF6B35), var(--tenant-gradient-end, #FF8E53))' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Enviando código...</>
                ) : 'Continuar'}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Te enviaremos un código de 6 dígitos para verificar tu identidad
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          ¿Sos cliente nuevo?{' '}
          <a
            href="mailto:soporte@johnfox.click"
            className="hover:underline font-medium"
            style={{ color: 'var(--tenant-primary, #FF6B35)' }}
          >
            Contactanos
          </a>
        </p>
      </div>
    </div>
  );
}
